import { useEffect, useRef, useState } from 'react';

import { useTranslations } from '../hooks/useTranslations';
import { translationKeys } from '../translations/translationKeys';
import Text from './Text';

console.log('testing OTP.tsx');

type OTPProps = {
  updateFieldValue?: (value: string) => void;
  triggerFieldChange?: (value: string) => void;
  disabled?: boolean;
  onSubmit?: () => void;
};

function OTP(props: OTPProps) {
  const [OTPValues, setOTPValues] = useState(Array.from({ length: 6 }, () => ''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { updateFieldValue } = props;
  const { translate } = useTranslations();
  useEffect(() => {
    // Focus the first input when the component mounts
    inputRefs.current?.[0]?.focus();
  }, []);

  useEffect(() => {
    const OTPFinal = OTPValues.join('');
    if (updateFieldValue) {
      updateFieldValue(OTPFinal);
    }
  }, [OTPValues, updateFieldValue]);

  useEffect(() => {
    function handlePaste(event: ClipboardEvent) {
      event.preventDefault();

      const pastedText = event.clipboardData?.getData('text') || '';
      const pastedTextArr = pastedText.replace(/\D/g, '').split('');
      const newValues = OTPValues.map((val, idx) => {
        return pastedTextArr[idx] || val;
      });
      setOTPValues(newValues);
    }

    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [OTPValues, setOTPValues]);

  useEffect(() => {
    function handleKeyPress(event: KeyboardEvent) {
      if (props.onSubmit && event.key === 'Enter') {
        event.preventDefault();
        if (inputRefs.current.some((input) => input?.matches(':focus'))) {
          props.onSubmit();
        }
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        const focusedInput = inputRefs.current.find((input) => input?.matches(':focus'));
        if (focusedInput) {
          const idx = inputRefs.current.indexOf(focusedInput);

          setOTPValues((prev) => {
            const newValues = [...prev];
            newValues[idx] = '';
            return newValues;
          });

          // Move focus to previous input if not the first
          if (idx > 0) {
            inputRefs.current[idx - 1]?.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [props]);

  return (
    <div>
      <p className="text-base font-medium mb-3">{translate(translationKeys.ENTER_YOUR_ONE_TIME_CODE)}</p>
      <div className="flex flex-row gap-2">
        {OTPValues.map((val, idx) => (
          <Text
            key={idx}
            ref={(el) => (inputRefs.current[idx] = el)}
            value={val}
            useValuePropOnly={true}
            type="number"
            noAutoComplete
            id={String(idx)}
            inputMode={'numeric'}
            updateFieldValue={(val) => {
              setOTPValues((prev) => {
                const newValues = [...prev];
                newValues[idx] = val.charAt(val.length - 1);
                return newValues;
              });

              // Auto-focus to next input when a character is entered
              if (val && idx < 5) {
                inputRefs.current[idx + 1]?.focus();
              }
            }}
            triggerFieldChange={(val) => {
              if (props.triggerFieldChange) {
                props.triggerFieldChange(val);
              }
            }}
            className="min-h-[52px] min-w-[47px] w-full py-2 px-4 sm:py-4 sm:px-2 text-center"
            disabled={props.disabled}
          />
        ))}
      </div>
    </div>
  );
}

export default OTP;
